import { config as validationConfig } from './validation';

validationConfig();

// Next 16 removes serverRuntimeConfig/publicRuntimeConfig (and getConfig()),
// so this module reads process.env directly on the server. Browser code can't
// read runtime env vars, so the server serializes `shared` into an inline
// script (window.__OLLIE_PUBLIC_ENV__, see pages/_document.js and
// app/layout.js) that runs before the app bundles, and the client builds
// `shared` from that. `server` values never leave the server.
const isServer = typeof window === 'undefined';

export const server = isServer
  ? {
      AWS_S3_REGION: process.env.AWS_S3_REGION,
      AWS_S3_ENDPOINT: process.env.AWS_S3_ENDPOINT,
      AWS_S3_GUIDELINES_BUCKET: process.env.AWS_S3_GUIDELINES_BUCKET,
      EVENTBRITE_API_KEY: process.env.EVENTBRITE_API_KEY,
      FACEBOOK_SHARE_APP_ID: process.env.FACEBOOK_SHARE_APP_ID,
      FB_APP_ID: process.env.FB_APP_ID,
      ITERABLE_API_KEY: process.env.ITERABLE_API_KEY,
      MAINTENANCE_MODE: process.env.MAINTENANCE_MODE === 'true',
      MUX_TOKEN_ID: process.env.MUX_TOKEN_ID,
      MUX_TOKEN_SECRET: process.env.MUX_TOKEN_SECRET,
      NEXTAUTH_JWT_ENCRYPTION_KEY: process.env.NEXTAUTH_JWT_ENCRYPTION_KEY,
      NEXTAUTH_JWT_SIGNING_KEY: process.env.NEXTAUTH_JWT_SIGNING_KEY,
      NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
      // Kill switch for the .next/cache size reporter (ES-2031). On by
      // default so no per-environment setup is needed to get readings; set to
      // 'false' per environment if the filesystem walk ever proves costly
      // (its own cost is reported as scan_duration_ms).
      NEXT_CACHE_METRICS_ENABLED:
        process.env.NEXT_CACHE_METRICS_ENABLED !== 'false',
      REDIS_HOST: process.env.REDIS_HOST,
      REDIS_PORT: process.env.REDIS_PORT,
      SHOPIFY_CLIENT_ID: process.env.SHOPIFY_CLIENT_ID,
      SHOPIFY_CLIENT_SECRET: process.env.SHOPIFY_CLIENT_SECRET,
      SHOPIFY_CUSTOMER_API_VERSION: process.env.SHOPIFY_CUSTOMER_API_VERSION,
      // Management API token (write access) — server-only. Lived in
      // publicRuntimeConfig historically, which shipped it to every browser
      // via __NEXT_DATA__; its only consumer is the storyblok-assets API route.
      STORYBLOK_API_OAUTH_TOKEN: process.env.STORYBLOK_API_OAUTH_TOKEN,
      // Shared with the CloudFront edge auth lambda, which checks it before
      // letting the Visual Editor iframe past SSO. Server-only: in `shared` it
      // would ship to every browser in __OLLIE_PUBLIC_ENV__. Unset in
      // production, which has no edge gate — setting it there would make the
      // preview handler demand a path prefix the Storyblok URL does not have.
      // Keep it path-safe (hex): it travels as a Storyblok preview URL segment.
      STORYBLOK_EDITOR_SECRET: process.env.STORYBLOK_EDITOR_SECRET,
      // The Storyblok space this environment serves, which the Visual Editor
      // request is checked against. Staging reads Staging; sandbox and
      // previews read Development. Defaults to Staging's id, the value every
      // environment compared against before this was configurable, so an
      // environment without the parameter keeps the behaviour it has today.
      STORYBLOK_SPACE_ID: process.env.STORYBLOK_SPACE_ID || '89894',
      STORYBLOK_WEBHOOK_SECRET: process.env.STORYBLOK_WEBHOOK_SECRET,
    }
  : {};

export const shared = isServer
  ? {
      API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
      API_HTTP_SCHEME:
        process.env.NEXT_PUBLIC_API_BASE_URL?.includes('local') &&
        process.env.LOCAL_API_USE_HTTPS !== 'true'
          ? 'http'
          : 'https',
      AWS_WAF_CHALLENGE_INTEGRATION_URL:
        process.env.AWS_WAF_CHALLENGE_INTEGRATION_URL,
      BRIGHTBACK_APP_ID: process.env.BRIGHTBACK_APP_ID,
      COMMIT_SHA: process.env.COMMIT_SHA,
      DATADOG_RUM_APPLICATION_ID: process.env.DATADOG_RUM_APPLICATION_ID,
      DATADOG_RUM_CLIENT_TOKEN: process.env.DATADOG_RUM_CLIENT_TOKEN,
      DATADOG_RUM_ENABLED_AND_LIST_PAGES:
        process.env.DATADOG_RUM_ENABLED_AND_LIST_PAGES,
      DATADOG_RUM_SESSION_RECORDING:
        process.env.DATADOG_RUM_SESSION_RECORDING === 'true',
      DEFAULT_OFFER_CODE: process.env.DEFAULT_OFFER_CODE,
      DEFAULT_REACTIVATION_OFFER_CODE:
        process.env.DEFAULT_REACTIVATION_OFFER_CODE,
      DIG_CONNECTION_ID: process.env.DIG_CONNECTION_ID,
      DIG_TOKEN: process.env.DIG_TOKEN,
      DIG_URL: process.env.DIG_URL,
      DOMAIN_PUBLIC_NAME: process.env.DOMAIN_PUBLIC_NAME,
      ELEVAR_CONFIG_ID: process.env.NEXT_PUBLIC_ELEVAR_CONFIG_ID,
      EXTOLE_CAMPAIGN_LABEL: process.env.EXTOLE_CAMPAIGN_LABEL,
      FLAGSHIP_API_KEY: process.env.FLAGSHIP_API_KEY,
      FLAGSHIP_ENV_ID: process.env.FLAGSHIP_ENV_ID,
      GLADLY_API_URL: process.env.GLADLY_API_URL,
      GLADLY_BRAND_ID: process.env.GLADLY_BRAND_ID,
      GLADLY_CDN: process.env.GLADLY_CDN,
      GLADLY_ORG_ID: process.env.GLADLY_ORG_ID,
      GLADLY_PUBLISHABLE_KEY: process.env.GLADLY_PUBLISHABLE_KEY,
      GOOGLE_RECAPTCHA_SITE_KEY: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
      ID_DOT_ME_CLIENT_ID: process.env.ID_DOT_ME_CLIENT_ID,
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
      NEXTAUTH_URL: process.env.NEXTAUTH_URL,
      OLLIE_ENV: process.env.NEXT_PUBLIC_OLLIE_ENV,
      SEGMENT_WRITE_KEY: process.env.SEGMENT_WRITE_KEY,
      STRIPE_PUBLISHABLE_KEY: process.env.STRIPE_PUBLISHABLE_KEY,
      SHOPIFY_AUTH_PROXY_URL: process.env.NEXT_PUBLIC_SHOPIFY_AUTH_PROXY_URL,
      SHOPIFY_SHOP_ID: process.env.SHOPIFY_SHOP_ID,
      SITE_NAME: process.env.SITE_NAME,
      STORYBLOK_API_ACCESS_TOKEN: process.env.STORYBLOK_API_ACCESS_TOKEN,
      STORYBLOK_REDIS_ENABLED: process.env.STORYBLOK_REDIS_ENABLED !== 'false',
      WEB_SENTRY_DSN: process.env.NEXT_PUBLIC_WEB_SENTRY_DSN,
    }
  : window.__OLLIE_PUBLIC_ENV__ ?? {};

/**
 * Inline-script source that exposes `shared` to the browser. Rendered into
 * the document head by both routers so it executes before any app bundle
 * reads `shared`. `<` is escaped so env values can never close the script tag.
 */
export const publicEnvScript = () =>
  `window.__OLLIE_PUBLIC_ENV__ = ${JSON.stringify(shared).replace(
    /</g,
    '\\u003c',
  )};`;
